import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Button, Spinner, Alert, Form } from 'react-bootstrap';
import { AxiosResponse } from 'axios';
import apiDefinitions from '../../../../core/api/apiDefinitions';
import { checkPermission } from '../../../../core/permissons/CheckPermissons';
import { showError, showSuccess } from '../../../../core/alert/sweetAlertConfig';

/** DTOs from list view (keep local to modal for clarity) */
interface EmployeeDTO {
  name: string;
  empNo: number | null;
  designation: string;
  email: string;
  phone: string;
  status: string | null;
  personType: string;
}
interface LocationDTO {
  id: number;
  name: string;
  address: string;
  lat: number;
  lng: number;
  description?: string;
  createdBy: string;
  createdAt: string;
  updatedBy?: string;
  updatedAt?: string;
}
interface DepartmentDTO {
  id: number;
  name: string;
  location: string;
  address: string;
  createdBy: string;
  createdAt: string;
}
interface VisitorDTO {
  id?: number;
  name: string;
  email: string;
  nic: string;
  phone: string;
  visitorCompany: string;
  arrivalTime: string;    // ISO string
  departureTime: string;  // ISO string
  visitorRequestId?: number;
}
interface VehicleDTO {
  id?: number;
  vehicleNumber: string;
  parkingSlotId?: number | null;
  arrivalTime: string;    // ISO string
  departureTime: string;  // ISO string
  visitorRequestId?: number;
}
interface VisitorRequestDTO {
  id: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  locationId: number;
  departmentId: number;
  parkingRequired: boolean;
  dateTime: string | number;  // from API can be iso/epoch
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}
interface VisitorRequestPayload {
  id: number;
  responsiblePersonEmpNo: string;
  visitorPosition: string;
  visitorReason: string;
  locationId: number;
  departmentId: number;
  parkingRequired: boolean;
  dateTime: number;
  status: string;
  visitors: VisitorDTO[];
  vehicles: VehicleDTO[];
}
interface GetEmployeesResponse {
  status: string;
  statusDescription: string;
  data: EmployeeDTO[];
  total: number | null;
}
interface GetLocationsResponse {
  status: string;
  data: { locations: LocationDTO[]; total: number };
}
interface GetDepartmentsResponse {
  status: string;
  data: { departments: DepartmentDTO[]; total: number };
}

type Props = {
  show: boolean;
  onHide: () => void;
  data: VisitorRequestDTO | null; // full object fetched before showing modal
  onUpdated: () => void;          // parent refresh hook on success
};

const EditVisitorRequestModal: React.FC<Props> = ({ show, onHide, data, onUpdated }) => {
  const [employees, setEmployees] = useState<EmployeeDTO[]>([]);
  const [locations, setLocations] = useState<LocationDTO[]>([]);
  const [departments, setDepartments] = useState<DepartmentDTO[]>([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [formData, setFormData] = useState<VisitorRequestDTO | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** seed form when incoming data changes */
  useEffect(() => {
    if (!data) return;
    // normalize dateTime to ISO in visitors/vehicles if missing
    const normalized: VisitorRequestDTO = {
      ...data,
      visitors: (data.visitors || []).map(v => ({
        ...v,
        arrivalTime: v.arrivalTime ? v.arrivalTime : '',
        departureTime: v.departureTime ? v.departureTime : '',
      })),
      vehicles: (data.vehicles || []).map(v => ({
        ...v,
        arrivalTime: v.arrivalTime ? v.arrivalTime : '',
        departureTime: v.departureTime ? v.departureTime : '',
      })),
    };
    setFormData(normalized);
  }, [data]);

  /** token helper */
  const checkToken = () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setError('Authentication token not found. Please log in.');
      return false;
    }
    return token;
  };

  /** bootstrap dropdown data (employees/locations/departments) */
  useEffect(() => {
    if (!show) return;            // only fetch when opening
    if (!checkToken()) return;

    const load = async () => {
      setIsBusy(true);
      setError(null);
      try {
        if (checkPermission('ROLE_GET_USERS')) {
          const res: AxiosResponse = await apiDefinitions.getEmployees();
          if (res.status === 200) {
            const payload: GetEmployeesResponse = res.data;
            setEmployees(payload.data || []);
          }
        }
        if (checkPermission('ROLE_GET_LOCATIONS')) {
          const res: AxiosResponse = await apiDefinitions.getLocations('1', { filterType: '', filterValue: '', date: '' });
          if (res.status === 200) {
            const payload: GetLocationsResponse = res.data;
            setLocations(payload.data?.locations || []);
          }
        }
        // departments (re-using GET_USERS permission like your page)
        if (checkPermission('ROLE_GET_USERS')) {
          const res: AxiosResponse = await apiDefinitions.getDepartments('1', { filterType: 'NAME', filterValue: '' });
          if (res.status === 200) {
            const payload: GetDepartmentsResponse = res.data;
            setDepartments(payload.data?.departments || []);
          }
        }
      } catch (e: any) {
        setError('Failed to load dropdown data.');
      } finally {
        setIsBusy(false);
      }
    };

    load();
  }, [show]);

  /** filter employees by query */
  const filteredEmployees = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return employees
      .filter(e =>
        (e.name?.toLowerCase().includes(q) || (e.empNo && String(e.empNo).includes(q))) &&
        e.status === 'ACTIVE'
      )
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [employees, searchQuery]);

  /** input handlers */
  const updateField = (field: keyof VisitorRequestDTO, value: any) => {
    setFormData(prev => (prev ? { ...prev, [field]: value } : prev));
  };

  const updateNested = (
    group: 'visitors' | 'vehicles',
    index: number,
    subField: keyof VisitorDTO | keyof VehicleDTO,
    value: any
  ) => {
    setFormData(prev => {
      if (!prev) return prev;
      const next = { ...prev };
      const list = [...(group === 'visitors' ? next.visitors : next.vehicles)];
      // @ts-ignore
      list[index] = { ...list[index], [subField]: value };
      if (group === 'visitors') next.visitors = list as VisitorDTO[];
      else next.vehicles = list as VehicleDTO[];
      return next;
    });
  };

  const addVisitor = () =>
    setFormData(prev => prev ? ({
      ...prev,
      visitors: [...prev.visitors, { name: '', email: '', nic: '', phone: '', visitorCompany: '', arrivalTime: '', departureTime: '' }]
    }) : prev);

  const removeVisitor = (index: number) =>
    setFormData(prev => prev ? ({ ...prev, visitors: prev.visitors.filter((_, i) => i !== index) }) : prev);

  const addVehicle = () =>
    setFormData(prev => prev ? ({
      ...prev,
      vehicles: [...prev.vehicles, { vehicleNumber: '', arrivalTime: '', departureTime: '' } as VehicleDTO]
    }) : prev);

  const removeVehicle = (index: number) =>
    setFormData(prev => prev ? ({ ...prev, vehicles: prev.vehicles.filter((_, i) => i !== index) }) : prev);

  /** validation */
  const validate = (): boolean => {
    if (!formData) { setError('Form not ready.'); return false; }
    if (!formData.id) { setError('Invalid visitor request ID.'); return false; }
    if (!formData.responsiblePersonEmpNo) { setError('Please select a responsible person.'); return false; }
    if (!formData.locationId) { setError('Please select a valid location.'); return false; }
    if (!formData.departmentId) { setError('Please select a valid department.'); return false; }
    if (!formData.visitorPosition) { setError('Please enter the visitor position.'); return false; }
    if (!formData.visitorReason) { setError('Please enter the visitor reason.'); return false; }
    if (!formData.visitors.length || !formData.visitors[0].name) {
      setError('Please add at least one visitor with a name.'); return false;
    }
    for (const v of formData.visitors) {
      if (!v.arrivalTime || !v.departureTime) {
        setError('Please provide arrival and departure times for all visitors.'); return false;
      }
    }
    if (formData.parkingRequired && formData.vehicles.length === 0) {
      setError('Please add at least one vehicle if parking is required.'); return false;
    }
    return true;
  };

  /** submit */
  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData) return;
    if (!checkToken()) return;
    if (!checkPermission('ROLE_UPDATE_VISITOR_REQUEST')) {
      showError('You do not have permission to update visitor requests.');
      return;
    }
    if (!validate()) return;

    try {
      setIsBusy(true);
      setError(null);

      const payload: VisitorRequestPayload = {
        id: formData.id,
        responsiblePersonEmpNo: formData.responsiblePersonEmpNo,
        visitorPosition: formData.visitorPosition,
        visitorReason: formData.visitorReason,
        locationId: Number(formData.locationId),
        departmentId: Number(formData.departmentId),
        parkingRequired: !!formData.parkingRequired,
        // use first visitor arrival as main dateTime (same logic as your page)
        dateTime: formData.visitors?.[0]?.arrivalTime ? new Date(formData.visitors[0].arrivalTime).getTime() : Date.now(),
        status: formData.status || 'PENDING',
        visitors: formData.visitors,
        vehicles: formData.vehicles,
      };

      const res: AxiosResponse = await apiDefinitions.updateVisitor(payload);
      if (res.status === 200) {
        showSuccess("Visitor request updated successfully");
        onUpdated(); // refresh parent tables/counters
        onHide();    // close modal
      } else {
        showError(res.data?.statusDescription || 'Failed to update visitor request');
      }
    } catch (e: any) {
      const msg = e.response?.data?.statusDescription || 'Failed to update visitor request. Please try again.';
      setError(msg);
      showError(msg);
    } finally {
      setIsBusy(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="xl" centered backdrop="static">
      <Form onSubmit={onSubmit}>
        <Modal.Header closeButton>
          <Modal.Title>Edit Visitor Request</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {isBusy && (
            <div className="d-flex align-items-center gap-2 mb-3">
              <Spinner animation="border" size="sm" /><span>Loading...</span>
            </div>
          )}

          {error && <Alert variant="danger">{error}</Alert>}

          {formData && (
            <div className="container-fluid px-0">
              <div className="row g-3">
                {/* Responsible Person */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Responsible Person</label>
                  <div className="position-relative">
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Search by name or employee number..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      disabled={isBusy}
                    />
                    <i className="ti ti-search" style={{ position: 'absolute', right: 12, top: 12 }} />
                  </div>
                  <select
                    className="form-select mt-2"
                    value={formData.responsiblePersonEmpNo}
                    onChange={(e) => updateField('responsiblePersonEmpNo', e.target.value)}
                    disabled={isBusy}
                  >
                    <option value="">Select an employee</option>
                    {filteredEmployees.map(emp => (
                      <option key={emp.empNo || emp.email} value={emp.empNo || emp.email}>
                        {emp.name} ({emp.empNo || 'N/A'})
                      </option>
                    ))}
                  </select>
                </div>

                {/* Location */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Location</label>
                  <select
                    className="form-select"
                    value={formData.locationId || ''}
                    onChange={(e) => updateField('locationId', Number(e.target.value))}
                    disabled={isBusy}
                  >
                    <option value="">Select a location</option>
                    {locations.map(loc => (
                      <option key={loc.id} value={loc.id}>{loc.name}</option>
                    ))}
                  </select>
                </div>

                {/* Department */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Department</label>
                  <select
                    className="form-select"
                    value={formData.departmentId || ''}
                    onChange={(e) => updateField('departmentId', Number(e.target.value))}
                    disabled={isBusy}
                  >
                    <option value="">Select a department</option>
                    {departments.map(dept => (
                      <option key={dept.id} value={dept.id}>{dept.name}</option>
                    ))}
                  </select>
                </div>

                {/* Visitor Position */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Visitor Position</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.visitorPosition}
                    onChange={(e) => updateField('visitorPosition', e.target.value)}
                    placeholder="e.g., Consultant"
                    disabled={isBusy}
                  />
                </div>

                {/* Visitor Reason */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Visitor Reason</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.visitorReason}
                    onChange={(e) => updateField('visitorReason', e.target.value)}
                    placeholder="e.g., Business Meeting"
                    disabled={isBusy}
                  />
                </div>

                {/* Parking Required */}
                <div className="col-12 col-md-6">
                  <label className="form-label">Parking Required</label>
                  <select
                    className="form-select"
                    value={formData.parkingRequired ? 'true' : 'false'}
                    onChange={(e) => updateField('parkingRequired', e.target.value === 'true')}
                    disabled={isBusy}
                  >
                    <option value="false">No</option>
                    <option value="true">Yes</option>
                  </select>
                </div>

                {/* Visitors */}
                <div className="col-12">
                  <h6 className="mt-2">Visitors</h6>
                  {formData.visitors.map((v, i) => (
                    <div key={i} className="border rounded p-3 mb-2">
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <strong>Visitor {i + 1}</strong>
                        {formData.visitors.length > 1 && (
                          <i className="ti ti-trash" role="button" onClick={() => removeVisitor(i)} title="Remove Visitor" />
                        )}
                      </div>
                      <div className="row g-2">
                        <div className="col-md-6">
                          <label className="form-label">Name</label>
                          <input className="form-control" value={v.name}
                                 onChange={(e) => updateNested('visitors', i, 'name', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Email</label>
                          <input type="email" className="form-control" value={v.email}
                                 onChange={(e) => updateNested('visitors', i, 'email', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">NIC</label>
                          <input className="form-control" value={v.nic}
                                 onChange={(e) => updateNested('visitors', i, 'nic', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Phone</label>
                          <input className="form-control" value={v.phone}
                                 onChange={(e) => updateNested('visitors', i, 'phone', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Company</label>
                          <input className="form-control" value={v.visitorCompany}
                                 onChange={(e) => updateNested('visitors', i, 'visitorCompany', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Arrival Time</label>
                          <input type="datetime-local" className="form-control"
                                 value={v.arrivalTime ? new Date(v.arrivalTime).toISOString().slice(0, 16) : ''}
                                 onChange={(e) => updateNested('visitors', i, 'arrivalTime', e.target.value)} />
                        </div>
                        <div className="col-md-6">
                          <label className="form-label">Departure Time</label>
                          <input type="datetime-local" className="form-control"
                                 value={v.departureTime ? new Date(v.departureTime).toISOString().slice(0, 16) : ''}
                                 onChange={(e) => updateNested('visitors', i, 'departureTime', e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  <Button variant="outline-primary" size="sm" onClick={addVisitor} disabled={isBusy}>
                    Add Another Visitor
                  </Button>
                </div>

                {/* Vehicles */}
                {formData.parkingRequired && (
                  <div className="col-12">
                    <h6 className="mt-3">Vehicles</h6>
                    {formData.vehicles.map((veh, i) => (
                      <div key={i} className="border rounded p-3 mb-2">
                        <div className="d-flex justify-content-between align-items-center mb-2">
                          <strong>Vehicle {i + 1}</strong>
                          <i className="ti ti-trash" role="button" onClick={() => removeVehicle(i)} title="Remove Vehicle" />
                        </div>
                        <div className="row g-2">
                          <div className="col-md-4">
                            <label className="form-label">Vehicle Number</label>
                            <input className="form-control" value={veh.vehicleNumber}
                                   onChange={(e) => updateNested('vehicles', i, 'vehicleNumber', e.target.value)} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Arrival Time</label>
                            <input type="datetime-local" className="form-control"
                                   value={veh.arrivalTime ? new Date(veh.arrivalTime).toISOString().slice(0,16) : ''}
                                   onChange={(e) => updateNested('vehicles', i, 'arrivalTime', e.target.value)} />
                          </div>
                          <div className="col-md-4">
                            <label className="form-label">Departure Time</label>
                            <input type="datetime-local" className="form-control"
                                   value={veh.departureTime ? new Date(veh.departureTime).toISOString().slice(0,16) : ''}
                                   onChange={(e) => updateNested('vehicles', i, 'departureTime', e.target.value)} />
                          </div>
                        </div>
                      </div>
                    ))}
                    <Button variant="outline-primary" size="sm" onClick={addVehicle} disabled={isBusy}>
                      Add Another Vehicle
                    </Button>
                  </div>
                )}
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide} disabled={isBusy}>Cancel</Button>
          <Button type="submit" variant="primary" disabled={isBusy}>
            {isBusy ? 'Updating...' : 'Update Request'}
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

export default EditVisitorRequestModal;
